import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, FileSignature, Loader2, PenLine, ShieldCheck } from "lucide-react";
import { loadPublicAgreement, signPublicAgreement } from "lib/carrierFlowApi";

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusTone(status) {
  const value = String(status || "").toLowerCase();
  if (/signed|complete|accepted/.test(value)) return "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-200";
  if (/expired|voided|cancelled|superseded|declined/.test(value)) return "border-red-400/30 bg-red-400/[0.08] text-red-200";
  return "border-gold/30 bg-gold/[0.08] text-accent-ink";
}

export default function AgreementSignPage() {
  const { token = "" } = useParams();
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const [form, setForm] = useState({
    signerName: "",
    signerEmail: "",
    signerTitle: "",
    signatureConsentAccepted: false,
  });
  const [submitState, setSubmitState] = useState({ status: "idle", message: "", error: "" });

  useEffect(() => {
    let mounted = true;
    setState({ status: "loading", data: null, error: "" });
    loadPublicAgreement(token).then((result) => {
      if (!mounted) return;
      if (!result.success) {
        setState({ status: "error", data: null, error: result.error || "This agreement link is unavailable." });
        return;
      }
      const agreement = result.data?.agreement || {};
      setState({ status: "ready", data: result.data, error: "" });
      setForm({
        signerName: agreement.signerName || "",
        signerEmail: agreement.signerEmail || "",
        signerTitle: agreement.signerTitle || "",
        signatureConsentAccepted: false,
      });
    });
    return () => {
      mounted = false;
    };
  }, [token]);

  const agreement = state.data?.agreement || {};
  const canSign = Boolean(state.data?.canSign);
  const signed = String(agreement.status || "").toLowerCase() === "signed";
  const previewHtml = useMemo(() => agreement.agreementHtml || "", [agreement.agreementHtml]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitState({ status: "loading", message: "", error: "" });
    const result = await signPublicAgreement(token, form);
    if (!result.success) {
      setSubmitState({ status: "error", message: "", error: result.error || "Agreement could not be signed." });
      return;
    }
    setState((current) => ({
      ...current,
      data: {
        ...(current.data || {}),
        canSign: false,
        agreement: {
          ...(current.data?.agreement || {}),
          ...(result.data?.agreement || {}),
        },
      },
    }));
    setSubmitState({ status: "signed", message: "Agreement signed successfully. A signed copy will appear in your customer access portal.", error: "" });
  };

  return (
    <main className="min-h-screen bg-canvas py-24 text-ink">
      <div className="site-content-shell">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-gold/25 bg-gold/[0.06] px-4 py-2 font-body text-[10px] font-semibold uppercase tracking-[0.24em] text-accent-ink">
            <FileSignature className="h-3.5 w-3.5" />
            Service Agreement
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">
            {agreement.companyName || "Magellan agreement"}
          </h1>
          <p className="mt-4 max-w-2xl font-body text-sm leading-7 text-copy/56">
            Review the document, confirm your signer details, and complete the electronic signature.
          </p>
        </motion.div>

        {state.status === "loading" ? (
          <div className="rounded-2xl border border-ink/10 bg-ink/[0.025] p-8">
            <div className="flex items-center gap-3 font-body text-copy/64">
              <Loader2 className="h-5 w-5 animate-spin text-accent-ink" />
              Loading agreement.
            </div>
          </div>
        ) : null}

        {state.status === "error" ? (
          <div className="rounded-2xl border border-ink/10 bg-ink/[0.025] p-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-1 h-5 w-5 text-accent-ink" />
              <div>
                <p className="font-body text-base leading-7 text-copy/68">{state.error}</p>
                <FlowButton to="/customer/access" className="mt-6 inline-flex items-center rounded-full  px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em]  transition ">
                  Open Customer Access
                </FlowButton>
              </div>
            </div>
          </div>
        ) : null}

        {state.status === "ready" ? (
          <div className="grid gap-6 lg:grid-cols-[1fr_390px]">
            <section className="overflow-hidden rounded-2xl border border-ink/10 bg-ink/[0.025]">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 px-5 py-4">
                <div>
                  <h2 className="font-display text-xl font-semibold text-ink">Agreement Preview</h2>
                  <p className="mt-1 font-body text-xs text-copy/42">Read the full document before signing.</p>
                </div>
                <span className={`inline-flex rounded-full border px-3 py-1 font-body text-[10px] font-bold uppercase tracking-[0.16em] ${statusTone(agreement.status)}`}>
                  {titleCase(agreement.status || "pending")}
                </span>
              </div>
              <div className="min-h-[720px] bg-ink text-black">
                {previewHtml ? (
                  <iframe title="Agreement preview" sandbox="" referrerPolicy="no-referrer" className="h-[720px] w-full bg-ink" srcDoc={previewHtml} />
                ) : (
                  <div className="whitespace-pre-wrap p-8 font-body text-sm leading-7">{agreement.agreementText || "Agreement preview unavailable."}</div>
                )}
              </div>
            </section>

            <aside className="space-y-4">
              <section className="rounded-2xl border border-ink/10 bg-ink/[0.025] p-5">
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full border border-gold/25 bg-gold/[0.07] text-accent-ink">
                    <ShieldCheck className="h-4 w-4" />
                  </span>
                  <h2 className="font-display text-xl font-semibold text-ink">Status</h2>
                </div>
                <div className="space-y-2 font-body text-sm text-copy/58">
                  <p>Published: {formatDate(agreement.publishedAt) || "Not available"}</p>
                  <p>Viewed: {formatDate(agreement.lastViewedAt || agreement.firstViewedAt) || "Not opened yet"}</p>
                  <p>Signed: {formatDate(agreement.signedAt) || "Not signed yet"}</p>
                  <p>Expires: {formatDate(agreement.expiresAt) || "No expiry shown"}</p>
                </div>
              </section>

              <form onSubmit={submit} className="rounded-2xl border border-ink/10 bg-ink/[0.025] p-5">
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full border border-gold/25 bg-gold/[0.07] text-accent-ink">
                    <PenLine className="h-4 w-4" />
                  </span>
                  <h2 className="font-display text-xl font-semibold text-ink">{signed ? "Signature Complete" : "Sign Agreement"}</h2>
                </div>
                <div className="space-y-3">
                  <input
                    value={form.signerName}
                    onChange={(event) => updateField("signerName", event.target.value)}
                    required
                    disabled={signed || !canSign}
                    className="min-h-[46px] w-full rounded-xl border border-ink/12 bg-black/24 px-4 font-body text-sm text-ink outline-none transition placeholder:text-copy/28 focus:border-gold/60 disabled:opacity-60"
                    placeholder="Full legal name"
                  />
                  <input
                    type="email"
                    value={form.signerEmail}
                    onChange={(event) => updateField("signerEmail", event.target.value)}
                    required
                    disabled={signed || !canSign}
                    className="min-h-[46px] w-full rounded-xl border border-ink/12 bg-black/24 px-4 font-body text-sm text-ink outline-none transition placeholder:text-copy/28 focus:border-gold/60 disabled:opacity-60"
                    placeholder="Business email"
                  />
                  <input
                    value={form.signerTitle}
                    onChange={(event) => updateField("signerTitle", event.target.value)}
                    disabled={signed || !canSign}
                    className="min-h-[46px] w-full rounded-xl border border-ink/12 bg-black/24 px-4 font-body text-sm text-ink outline-none transition placeholder:text-copy/28 focus:border-gold/60 disabled:opacity-60"
                    placeholder="Title / role"
                  />
                  <label className="flex items-start gap-3 rounded-xl border border-ink/10 bg-black/20 p-4 font-body text-sm leading-6 text-copy/62">
                    <input
                      type="checkbox"
                      checked={form.signatureConsentAccepted}
                      onChange={(event) => updateField("signatureConsentAccepted", event.target.checked)}
                      required={!signed}
                      disabled={signed || !canSign}
                      className="mt-1"
                    />
                    <span>I reviewed the agreement, am authorized to sign, and consent to this electronic signature record.</span>
                  </label>
                </div>

                {!canSign && !signed ? (
                  <div className="mt-4 rounded-xl border border-gold/20 bg-gold/[0.05] px-4 py-3 font-body text-sm leading-6 text-accent-ink/84">
                    This agreement is not currently available for signing. It may be waiting on another signer, approval, or a new link.
                  </div>
                ) : null}
                {submitState.message ? <p className="mt-4 font-body text-sm leading-6 text-emerald-200">{submitState.message}</p> : null}
                {submitState.error ? <p className="mt-4 font-body text-sm leading-6 text-red-300">{submitState.error}</p> : null}
                <FlowButton
                  type="submit"
                  disabled={submitState.status === "loading" || signed || !canSign}
                  className="mt-5 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full  px-6 font-body text-xs font-bold uppercase tracking-[0.14em]  transition  disabled:opacity-60"
                >
                  {submitState.status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : signed ? <CheckCircle2 className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
                  {signed ? "Agreement Signed" : "Sign Agreement"}
                </FlowButton>
              </form>
            </aside>
          </div>
        ) : null}
      </div>
    </main>
  );
}
